import type { TFunction } from 'i18next'

import { formatPhone } from '#/shared/lib/format'
import type { TimelineCall } from '#/shared/api/generated/communication/models'

const SECONDS_PER_MINUTE = 60

/** A genuinely missed call — an inbound call no one picked up (rendered in red). */
export const MISSED_CALL_STATUSES = new Set(['missed'])

/** We called out but the other side didn't pick up — shown as "No answer", not
 *  as a missed call (it's not an answered call, but it isn't a red miss either). */
export const NO_ANSWER_STATUSES = new Set(['no_answer', 'busy'])

/** An inbound call the agent actively declined — rendered with the missed-call
 *  glyph but in neutral gray (it wasn't simply missed; it was turned down). */
export const DECLINED_CALL_STATUSES = new Set(['rejected'])

/** Talk-time label, e.g. 102 → "1m 42s". Empty when there is no duration. */
export function callDurationLabel(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return ''
  const mins = Math.floor(seconds / SECONDS_PER_MINUTE)
  const secs = seconds % SECONDS_PER_MINUTE
  return `${mins}m ${secs.toString().padStart(2, '0')}s`
}

/** "(929) 592-3003 → (929) 123-4567" from a call's caller/callee numbers. */
export function callRoute(call: TimelineCall): string {
  const from = formatPhone(call.caller_number)
  const to = formatPhone(call.callee_number)
  if (!from && !to) return ''
  return `${from} → ${to}`
}

/**
 * Direction/outcome prefix for a call subtitle. A genuine miss reads "Missed";
 * everything else (including an unanswered outbound call) reads by direction —
 * the gray chip and the absent talk-time already signal the no-answer outcome.
 */
function callPrefix(call: TimelineCall, t: TFunction): string {
  if (MISSED_CALL_STATUSES.has(call.status)) return t('phonePanel.call.missed')
  if (call.direction === 'outbound') {
    return call.ai_handled
      ? t('phonePanel.call.aiOutbound')
      : t('phonePanel.call.outbound')
  }
  return t('phonePanel.call.inbound')
}

/**
 * Full call subtitle: prefix · duration · route, skipping empty parts. Pass
 * `route` to override the call's own caller→callee route — mainline rows supply
 * the counterparty→mainline route so the shared line stays visible.
 */
export function callSubtitle(
  call: TimelineCall,
  t: TFunction,
  route?: string | null,
): string {
  return [
    callPrefix(call, t),
    callDurationLabel(call.duration_seconds),
    route ?? callRoute(call),
  ]
    .filter(Boolean)
    .join(' · ')
}
